"use client";

import type {
  SelectProps as RadixSelectProps,
  SelectValueProps as RadixSelectValueProps,
} from "@radix-ui/react-select";
import * as RadixSelect from "@radix-ui/react-select";
import clsx from "clsx";
import { forwardRef, useId, useSyncExternalStore } from "react";
import { Icon } from "../Icon";

type Option = {
  label: string;
  value: string;
};

type SelectProps = {
  label?: string;
  description?: string;
  error?: string;
  size?: "sm" | "md" | "lg";
  block?: boolean;
} & Omit<RadixSelectProps, "dir"> &
  Omit<RadixSelectValueProps, "asChild"> & {
    options: Option[];
  };

// Shared MediaQueryList so many <Select> instances on the same page
// reuse one listener. useSyncExternalStore also gives an explicit SSR
// snapshot (`false`) and a safe fallback when matchMedia is absent
// (jsdom, older environments).
let sharedHoverMql: MediaQueryList | null = null;
function getHoverMql(): MediaQueryList | null {
  if (sharedHoverMql) return sharedHoverMql;
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return null;
  }
  sharedHoverMql = window.matchMedia("(hover: hover)");
  return sharedHoverMql;
}

function subscribeCanHover(cb: () => void) {
  const mql = getHoverMql();
  if (!mql) return () => {};
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
}

const getCanHoverSnapshot = () => getHoverMql()?.matches ?? false;
const getCanHoverServerSnapshot = () => false;

function useCanHover() {
  return useSyncExternalStore(
    subscribeCanHover,
    getCanHoverSnapshot,
    getCanHoverServerSnapshot,
  );
}

const styles = {
  trigger: {
    base: "flex relative items-center cursor-pointer font-medium text-gray-1200 disabled:text-gray-900 border border-default hover:bg-gray-300 bg-white disabled:bg-gray-400 disabled:pointer-events-none gap-2 outline-none focus-visible:ring-2 ring-offset-1 ring-theme-accent transition duration-150 ease-in-out",
    variants: {
      size: {
        sm: "h-9 px-3 rounded-md",
        md: "h-10 px-4 rounded-md",
        lg: "h-12 px-4 rounded-md",
      },
      block: "w-full justify-between",
    },
  },
};

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      size = "md",
      block = false,
      options,
      defaultValue,
      value,
      onValueChange,
      defaultOpen,
      open,
      onOpenChange,
      name,
      disabled,
      required,
      placeholder,
      label,
      description,
      error,
      id,
    },
    ref,
  ) => {
    const canHover = useCanHover();
    // The label is only a label if it points at the trigger's id. Callers
    // rarely pass one, so make one up: without it the trigger is a nameless
    // "combobox" to assistive tech and to getByRole queries.
    const generatedId = useId();
    const triggerId = id ?? name ?? generatedId;
    return (
      <div>
        {label && (
          <label
            className="text-gray-1200 mb-1.5 block text-sm font-medium"
            htmlFor={triggerId}
          >
            {label}
          </label>
        )}
        <RadixSelect.Root
          defaultValue={defaultValue}
          value={value}
          onValueChange={onValueChange}
          defaultOpen={defaultOpen}
          open={open}
          onOpenChange={onOpenChange}
          name={name}
          disabled={disabled}
          required={required}
        >
          <RadixSelect.Trigger
            ref={ref}
            id={triggerId}
            className={clsx([
              styles.trigger.base,
              styles.trigger.variants.size[size],
              block && styles.trigger.variants.block,
            ])}
          >
            <RadixSelect.Value placeholder={placeholder} />
            <RadixSelect.Icon asChild>
              <Icon
                icon="CaretDownSolid"
                className="text-sm text-black opacity-75"
              />
            </RadixSelect.Icon>
          </RadixSelect.Trigger>
          <RadixSelect.Portal>
            <RadixSelect.Content className="overflow-visible rounded-md border border-gray-600 bg-white p-1 z-50">
              {canHover && (
                <RadixSelect.ScrollUpButton className="relative flex h-6 items-center justify-center focus-visible:bg-gray-300">
                  <Icon icon="CaretUpSolid" className="text-sm text-black" />
                </RadixSelect.ScrollUpButton>
              )}
              <RadixSelect.Viewport>
                {options.map(({ value, label }) => {
                  // TODO: Accept groups and separators
                  return (
                    <RadixSelect.Item
                      className="relative flex h-8 items-center justify-between gap-4 rounded-sm px-3 font-medium outline-none hover:bg-gray-300 focus-visible:bg-gray-300"
                      value={value}
                      key={value}
                    >
                      <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
                      <RadixSelect.ItemIndicator asChild>
                        <Icon
                          icon="CircleCheckSolid"
                          className="text-theme-accent"
                        />
                      </RadixSelect.ItemIndicator>
                    </RadixSelect.Item>
                  );
                })}
              </RadixSelect.Viewport>
              {canHover && (
                <RadixSelect.ScrollDownButton className="relative flex h-6 items-center justify-center focus-visible:bg-gray-400">
                  <Icon icon="CaretDownSolid" className="text-sm text-black" />
                </RadixSelect.ScrollDownButton>
              )}
            </RadixSelect.Content>
          </RadixSelect.Portal>
        </RadixSelect.Root>
        {error && (
          <span className="text-signal-error mt-1 block text-sm font-medium">
            {error}
          </span>
        )}
        {!error && description && (
          <span className="text-gray-1000 mt-1 block text-sm font-medium">
            {description}
          </span>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";
